// INC2-CONTACT: the Catalog (integrations design §2.10, F4): one card per provider with its state —
// Connect · Connected (n) · "Not set up on this server" (the env keys behind an (i)) · "Waiting
// on review" for founder-gated access (behind an (i)). Connect opens a modal: the service's access
// tiers and its sign-in, or the custom API key form. Scopes and env keys sit behind (i) icons.
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoTip,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { PlugIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { LoadError, Loading } from "../screen-parts";
import { ProviderRow } from "../subscriptions/oauth";
import { ConnectionForm } from "../subscriptions/parts";
import { type Connection, type Provider, providersQuery } from "../subscriptions/queries";

export function Catalog({
  api,
  co,
  slug,
  scope,
  connections,
  disabled,
  kind,
}: {
  api: Api;
  co: string;
  slug: string;
  scope: "person" | "company";
  /** The caller's connections in this scope (for "Connected (n)"). */
  connections: Connection[];
  /** The company has tool connections off: Connect is disabled. */
  disabled: boolean;
  /** CONNECTIONS-AREA: only sign-in apps (`oauth`) or only keys (`api_key`); all when unset. */
  kind?: Provider["kind"];
}) {
  const q = useQuery(providersQuery(api, co));
  const [connecting, setConnecting] = useState<Provider | null>(null);
  if (q.isPending) return <Loading label="Loading the catalog" rows={3} />;
  if (q.isError) return <LoadError what="Catalog" error={q.error} />;
  const live = connections.filter((c) => c.scope === scope && c.state !== "disconnected");
  const providers = kind ? q.data.data.filter((p) => p.kind === kind) : q.data.data;
  return (
    <>
      <ul
        aria-label="Catalog"
        data-slot="catalog"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        {providers.map((p) => {
          const n = live.filter((c) => c.provider === p.key).length;
          const gated = p.tiers.filter((t) => t.founder_gated);
          return (
            <li
              key={p.key}
              data-provider={p.key}
              className="flex flex-col gap-2 rounded-lg border border-border p-3"
            >
              <div className="flex items-center gap-2">
                <PlugIcon aria-hidden="true" className="size-4 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                <InfoTip label={`About ${p.name}`}>
                  {p.summary}
                  {p.tiers.length ? ` Access: ${p.tiers.map((t) => t.label).join(", ")}.` : ""}
                </InfoTip>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-slot="catalog-state">
                {n > 0 ? <Badge variant="success">Connected ({n})</Badge> : null}
                {!p.configured ? (
                  <span className="flex items-center gap-1">
                    <Badge variant="warning">Not set up on this server</Badge>
                    <InfoTip label={`Setting up ${p.name}`}>
                      The server needs its OAuth app first:{" "}
                      <span className="font-mono">{p.setup_env.join(", ")}</span>
                    </InfoTip>
                  </span>
                ) : null}
                {p.configured && gated.length ? (
                  <span className="flex items-center gap-1">
                    <Badge variant="neutral">Waiting on review</Badge>
                    <InfoTip label={`${p.name} review`}>
                      {gated.map((t) => t.label).join(", ")}: opens once the vendor's review is
                      done.
                    </InfoTip>
                  </span>
                ) : null}
              </div>
              <div className="mt-auto">
                <Button
                  size="sm"
                  variant={n > 0 ? "secondary" : "default"}
                  disabled={disabled || !p.configured}
                  onClick={() => setConnecting(p)}
                >
                  {n > 0 ? "Connect another" : "Connect"}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      <Dialog open={connecting !== null} onOpenChange={(o) => (o ? null : setConnecting(null))}>
        <DialogContent data-slot="connect-dialog" className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Connect {connecting?.name}</DialogTitle>
            <DialogDescription>
              {scope === "company" ? "A company connection" : "Your own connection"}
            </DialogDescription>
          </DialogHeader>
          {connecting?.kind === "api_key" ? (
            <ConnectionForm
              api={api}
              co={co}
              scope={scope}
              disabled={disabled}
              onDone={() => setConnecting(null)}
            />
          ) : connecting ? (
            <div className="rounded-lg border border-border">
              <ProviderRow
                api={api}
                co={co}
                slug={slug}
                scope={scope}
                p={connecting}
                disabled={disabled}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
